import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MANUAL_REVIEW_QUEUE_QUERY_KEY,
  MANUAL_REVIEW_REVIEWERS_QUERY_KEY,
  manualReviewApi,
} from '../../api/admin';
import { messageFromOrchestratorAxiosError } from '../../lib/axiosErrorMessage';

const RETURN_TO_QUEUE = '';

type Props = {
  applicationId: string;
  open: boolean;
  onClose: () => void;
};

export function ManualReviewReassignDialog({ applicationId, open, onClose }: Props) {
  const qc = useQueryClient();
  const [target, setTarget] = useState(RETURN_TO_QUEUE);

  const reviewersQ = useQuery({
    queryKey: MANUAL_REVIEW_REVIEWERS_QUERY_KEY,
    queryFn: () => manualReviewApi.reviewers(),
    enabled: open,
    retry: false,
  });

  const reassign = useMutation({
    mutationFn: () =>
      manualReviewApi.reassign(
        applicationId,
        target ? { toReviewerEmail: target } : {},
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: MANUAL_REVIEW_QUEUE_QUERY_KEY });
      await qc.invalidateQueries({ queryKey: ['manualReviewAssignment', applicationId] });
      await qc.invalidateQueries({ queryKey: MANUAL_REVIEW_REVIEWERS_QUERY_KEY });
      onClose();
      setTarget(RETURN_TO_QUEUE);
    },
  });

  const reviewers = reviewersQ.data?.reviewers ?? [];
  const rosterDown = reviewersQ.data?.rosterAvailable === false;
  const error = reassign.isError ? messageFromOrchestratorAxiosError(reassign.error) : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reassign review</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Move this file to another loan reviewer, or return it to the front of the queue. A
            reviewer already holding a file cannot take another.
          </Typography>
          {rosterDown ? (
            <Alert severity="warning">The reviewer roster is unavailable. You can still return the file to the queue.</Alert>
          ) : null}
          <FormControl fullWidth size="small">
            <InputLabel id="reassign-target-label">Give to</InputLabel>
            <Select
              labelId="reassign-target-label"
              label="Give to"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            >
              <MenuItem value={RETURN_TO_QUEUE}>Return to queue (front of the line)</MenuItem>
              {reviewers.map((r) => {
                const busy = Boolean(r.holdingApplicationId && r.holdingApplicationId !== applicationId);
                // Off-shift is disabled, not just labelled: the API refuses it, because the sweep
                // enforces "assigned implies on shift" and would otherwise bounce the file straight
                // back to the queue. Better to grey it out than to let the supervisor pick it and
                // read an error afterwards.
                return (
                  <MenuItem key={r.email} value={r.email} disabled={busy || !r.onShift}>
                    {r.displayName || r.email}
                    {r.onShift ? '' : ' — off shift'}
                    {busy ? ' — already holding a file' : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={reassign.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={reassign.isPending}
          onClick={() => reassign.mutate()}
        >
          {reassign.isPending ? 'Reassigning…' : 'Reassign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
